// Levi onboarding · shared copy (Feb 2026 · iter36).
//
// Single source of truth for every Levi introduction surface:
//   * Dashboard "Meet Levi" card
//   * Dedicated /app/meet-levi page
//   * Estimate Editor "What is Levi?" popover
//   * Sidebar nav label
//
// One dictionary means all three surfaces stay in sync — no drift when
// wording is tuned later. Wording approved by Rio (Feb 2026).
//
// This module is a pure copy dictionary — zero side effects, zero
// imports, zero network calls.

// V-P1 polish · Feb 2026. Product Owner locked contractor-facing
// identity: "Levi is the contractor's coworker at JobClose AI." Do
// NOT describe Levi as "AI Project Manager", "AI Assistant", or
// chatbot on contractor-facing surfaces. The old wording remains
// only inside internal engineering comments for historical context.
export const LEVI_INTRO_TITLE = "Meet Levi";

export const LEVI_INTRO_SUBTITLE =
  "Your coworker at JobClose AI. Give me a job to work on, ask me to find something, or tell me what you need help with — I'll help you move the work forward, and you make the decisions.";

// V-P1 · Meet Levi capability content — contractor-facing "wow" list.
// PO-approved direction: the contractor should react "I didn't know he
// could do that." Descriptions must match capabilities that already
// ship in JobClose AI. Contractor-approval boundaries preserved.
export const LEVI_INTRO_DOES = [
  "Walk the job with you using Vision — talk me through what you see",
  "Verify measurements and scope from the info you give me",
  "Build estimates from your approved pricing — I never invent rates",
  "Prepare proposals, contracts, invoices, material takeoffs, work orders, and closeout documents for your review",
  "Draft customer emails and texts through your approved delivery workflows",
  "Schedule appointments and set reminders (e.g. \"remind me 30 min before\")",
  "Work in English and Spanish, conversationally, spoken or typed",
  "Pull up any customer, project, or document without menu hunting",
  "Surface what needs your attention across every open job",
  "Show projected and actual profitability alongside the work",
  "Help with unusual Custom Jobs and share my recommendation — you decide",
];

// Contractor-facing bullet list — WHAT Levi does NOT do. This section is
// non-negotiable: it's how contractors know Levi will not silently change
// pricing or embarrass them in front of a customer.
export const LEVI_INTRO_DOES_NOT = [
  "Does not change pricing automatically",
  "Does not edit line items automatically",
  "Does not send anything to a customer without your explicit approval",
  "Does not replace contractor approval on anything",
  "Does not appear on customer-facing proposals, PDFs, contracts, invoices, or public pages",
  "Only acts on your behalf, through workflows you've approved",
];

// Short blurb for the sidebar tooltip / card teaser.
export const LEVI_INTRO_TEASER =
  "Your coworker at JobClose AI. Help with intake, estimates, and follow-up — always through workflows you've approved.";

// Deep-link destination for the "Learn more" affordances.
export const LEVI_INTRO_HREF = "/app/meet-levi";

// V-P1 polish · Feb 2026. Spanish parity for the coworker identity.
export const LEVI_INTRO_TITLE_ES = "Conoce a Levi";

export const LEVI_INTRO_SUBTITLE_ES =
  "Tu compañero de trabajo en JobClose AI. Dame un trabajo, pídeme que encuentre algo o dime en qué necesitas ayuda — yo te ayudo a mover el trabajo, y tú tomas las decisiones.";

export const LEVI_INTRO_DOES_ES = [
  "Recorro el trabajo contigo usando Vision — háblame de lo que ves",
  "Verifico medidas y alcance a partir de la información que me das",
  "Preparo estimados con tus precios aprobados — nunca invento tarifas",
  "Preparo propuestas, contratos, facturas, cálculos de materiales, órdenes de trabajo y documentos de cierre para tu revisión",
  "Redacto correos y mensajes al cliente a través de tus flujos aprobados",
  "Agendo citas y creo recordatorios (por ejemplo, \"recuérdame 30 min antes\")",
  "Trabajo en inglés y español, conversacionalmente, hablado o escrito",
  "Localizo cualquier cliente, proyecto o documento sin buscar en menús",
  "Resalto lo que necesita tu atención en todos los trabajos abiertos",
  "Muestro rentabilidad proyectada y real junto al trabajo",
  "Ayudo con Trabajos a Medida y comparto mi recomendación — tú decides",
];

export const LEVI_INTRO_DOES_NOT_ES = [
  "No cambia los precios automáticamente",
  "No edita partidas automáticamente",
  "No envía propuestas",
  "No sustituye la aprobación del contratista",
  "No aparece en propuestas, PDFs, contratos, facturas o páginas públicas para el cliente",
  "No actúa fuera del lado del contratista de la aplicación",
];

// Updated "Where to find Levi" bar copy — Slice 2F1 reinforces that
// Levi is a team member on the workspace, not a chatbot in the corner.
export const LEVI_WHERE_TO_FIND =
  "Levi lives on your contractor workspace. Click him whenever you need help with job intake, estimates, scope questions, or next steps. Levi remains on the contractor side and never appears on customer-facing proposals, contracts, invoices, PDFs, or public pages.";

export const LEVI_WHERE_TO_FIND_ES =
  "Levi vive en tu espacio de trabajo del contratista. Haz clic en él cuando necesites ayuda con la toma del trabajo, estimados, preguntas de alcance o próximos pasos. Levi permanece del lado del contratista y nunca aparece en propuestas, contratos, facturas, PDFs o páginas públicas para el cliente.";

// LocalStorage keys used by dismissible surfaces. Centralized so no
// component invents its own key.
export const LEVI_INTRO_DISMISSED_KEY = "jc.levi.intro.dismissed";

// Meet Levi · "Try asking me" example prompts. NON-INTERACTIVE.
// These are educational examples only — they help a contractor see
// the shape of questions Levi can answer. They do NOT open the drawer
// with prefilled text, they do NOT trigger the /command pipeline, and
// they intentionally do not exist as a new prompt system. Any future
// clickable/prefilled variant would be a separate feature authorized
// by PO. See §8 of the Meet Levi read-only inventory.
export const LEVI_TRY_ASKING = [
  "What jobs are waiting on me?",
  "Show me today's schedule.",
  "Draft a proposal for the Johnson roof repair.",
  "What's my projected profit this month?",
];

export const LEVI_TRY_ASKING_ES = [
  "¿Qué trabajos están esperando por mí?",
  "Muéstrame la agenda de hoy.",
  "Redacta una propuesta para la reparación del techo de Johnson.",
  "¿Cuál es mi ganancia proyectada este mes?",
];

export const readIntroDismissed = () => {
  try {
    return localStorage.getItem(LEVI_INTRO_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

export const writeIntroDismissed = (v) => {
  try {
    localStorage.setItem(LEVI_INTRO_DISMISSED_KEY, v ? "1" : "0");
  } catch {
    /* best effort only */
  }
};
